import axios, { AxiosResponse } from "axios";
import { appConfig } from "../config/keycloak";

/**
 * Service to handle Deal operations
 * Usa configuración centralizada de keycloak.js
 */
class DealService {
    private readonly api = axios.create({
        baseURL: appConfig.baseUrl // Usa configuración centralizada
    });

    constructor() {
        // Empty constructor, configuration is in the api instance
    }

    /**
     * Get all deals with pagination
     */
    public getAllDeals(
        token: string,
        page: number = 0,
        size: number = 10
    ): Promise<AxiosResponse<any>> {
        return this.api.get(
            `/deal-service/api/deals/all?page=${page}&size=${size}&sort=createdDate,desc`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * Get deal by public ID
     */
    public getDealByPublicId(
        token: string,
        dealPublicId: string
    ): Promise<AxiosResponse<any>> {
        return this.api.get(
            `/deal-service/api/deals/by-public/${dealPublicId}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * Get deal by private ID
     */
    public getDealByPrivateId(
        token: string,
        dealPrivateId: string
    ): Promise<AxiosResponse<any>> {
        return this.api.get(
            `/deal-service/api/deals/${dealPrivateId}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * Get deal events by public ID
     */
    public getDealEvents(
        token: string,
        dealPublicId: string,
        page: number = 0,
        size: number = 5
    ): Promise<AxiosResponse<any>> {
        return this.api.get(
            `/deal-service/api/deals/by-public/${dealPublicId}/events?page=${page}&size=${size}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * General search
     */
    public searchDeals(
        token: string,
        query: string,
        page: number = 0,
        size: number = 10
    ): Promise<AxiosResponse<any>> {
        return this.api.get(
            `/deal-service/api/deals?q=${encodeURIComponent(query)}&page=${page}&size=${size}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * Search by status
     */
    public searchDealsByStatus(
        token: string,
        statuses: string[],
        page: number = 0,
        size: number = 20
    ): Promise<AxiosResponse<any>> {
        const statusParams = statuses.map(status => `status=${status}`).join('&');
        return this.api.get(
            `/deal-service/api/deals?${statusParams}&page=${page}&size=${size}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * Search by legal name
     */
    public searchDealsByLegalName(
        token: string,
        legalName: string,
        page: number = 0,
        size: number = 10
    ): Promise<AxiosResponse<any>> {
        return this.api.get(
            `/deal-service/api/deals?legalName=${encodeURIComponent(legalName)}&page=${page}&size=${size}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * Search by tax ID
     */
    public searchDealsByTaxId(
        token: string,
        federalTaxId: string
    ): Promise<AxiosResponse<any>> {
        return this.api.get(
            `/deal-service/api/deals?federalTaxId=${encodeURIComponent(federalTaxId)}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * Search by date range
     */
    public searchDealsByDateRange(
        token: string,
        createdAtFrom: string,
        createdAtTo: string,
        sort: string = 'createdDate,desc',
        page: number = 0,
        size: number = 10
    ): Promise<AxiosResponse<any>> {
        return this.api.get(
            `/deal-service/api/deals?createdAtFrom=${createdAtFrom}&createdAtTo=${createdAtTo}&sort=${sort}&page=${page}&size=${size}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * Combined advanced search
     */
    public advancedSearchDeals(
        token: string,
        searchParams: {
            q?: string;
            q_operator?: string;
            status?: string[];
            substatus?: string[];
            legalName?: string;
            federalTaxId?: string;
            createdBy?: string;
            createdAtFrom?: string;
            createdAtTo?: string;
            sort?: string;
            page?: number;
            size?: number;
        }
    ): Promise<AxiosResponse<any>> {
        const params = new URLSearchParams();

        if (searchParams.q) {
            params.append('q', searchParams.q);
        }
        if (searchParams.q_operator) {
            params.append('q_operator', searchParams.q_operator);
        }
        if (searchParams.status && searchParams.status.length > 0) {
            searchParams.status.forEach(status => params.append('status', status));
        }
        if (searchParams.substatus && searchParams.substatus.length > 0) {
            searchParams.substatus.forEach(substatus => params.append('substatus', substatus));
        }
        if (searchParams.legalName) {
            params.append('legalName', searchParams.legalName);
        }
        if (searchParams.federalTaxId) {
            params.append('federalTaxId', searchParams.federalTaxId);
        }
        if (searchParams.createdBy) {
            params.append('createdBy', searchParams.createdBy);
        }
        if (searchParams.createdAtFrom) {
            params.append('createdAtFrom', searchParams.createdAtFrom);
        }
        if (searchParams.createdAtTo) {
            params.append('createdAtTo', searchParams.createdAtTo);
        }
        if (searchParams.sort) {
            params.append('sort', searchParams.sort);
        }

        params.append('page', (searchParams.page || 0).toString());
        params.append('size', (searchParams.size || 15).toString());

        return this.api.get(
            `/deal-service/api/deals?${params.toString()}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * Evaluate deal for underwriting using mock endpoint (DEPRECATED)
     */
    public evaluateDealMock(token: string): Promise<AxiosResponse<any>> {
        return this.api.post(
            `/application-service/api/v1/offers/evaluate`,
            { text: "request" },
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * Evaluate deal for underwriting
     */
    public evaluateDeal(token: string, evaluationPayload: any): Promise<AxiosResponse<any>> {
        const ENGINE_USER = "user_93mxtq";
        const ENGINE_PASSWORD = "Nf7@pY8vRz!sZ1Lx";

        const WEBHOOK_PATH = '/webhook/1d71e787-3eb4-4d82-87d6-db9b41015911';
        const ENGINE_URL = `${appConfig.n8nUrl}${WEBHOOK_PATH}`;

        const credentials = btoa(`${ENGINE_USER}:${ENGINE_PASSWORD}`);

        console.log("🔗 [evaluateDeal] n8n webhook URL: ", ENGINE_URL);
        console.log("📦 [evaluateDeal] payload: ", evaluationPayload);
        
        return axios.post(
            ENGINE_URL,
            evaluationPayload,
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Basic ${credentials}`,
                    'Cache-Control': 'no-cache'
                }
            }
        );
    }

    /**
     * Update deal status
     */
    public updateDealStatus(
        token: string,
        dealPrivateId: string,
        toStatus: string
    ): Promise<AxiosResponse<any>> {
        return this.api.post(
            `/deal-service/api/deals/${dealPrivateId}/status`,
            { toStatus },
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * Update deal substatus
     */
    public updateDealSubstatus(
        token: string,
        dealPrivateId: string,
        toSubstatus: string
    ): Promise<AxiosResponse<any>> {
        return this.api.patch(
            `/deal-service/api/deals/${dealPrivateId}/substatus`,
            { substatus: toSubstatus },
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    

    /**
     * Create deal offer
     */
    public createDealOffer(
        token: string,
        dealPrivateId: string,
        offer: {
            term_duration_months?: number;
            gross_payback?: number;
            gross_advance_amount?: number;
            net_payback?: number;
            monthly_payment?: number;
            advance_amount?: number;
            factor?: number;
            processing_fee?: number;
            note?: string;
            flowId?: string;
            reference_id?: string;
        }
    ): Promise<AxiosResponse<any>> {
        return this.api.post(
            `/deal-service/api/deals/${dealPrivateId}/offers`,
            offer,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * Update deal offer
     */
    public updateDealOffer(
        token: string,
        dealPrivateId: string,
        offerId: string,
        offer: {
            term_duration_months?: number;
            gross_payback?: number;
            gross_advance_amount?: number;
            net_payback?: number;
            monthly_payment?: number;
            advance_amount?: number;
            factor?: number;
            processing_fee?: number;
            note?: string;
            flowId?: string;
            // reference_id?: string;
        }
    ): Promise<AxiosResponse<any>> {
        return this.api.put(
            `/deal-service/api/deals/${dealPrivateId}/offers/${offerId}`,
            offer,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * Get deal offers
     */
    public getDealOffers(
        token: string,
        dealPrivateId: string,
        page: number = 0,
        size: number = 10,
        sortField: string = 'created_at',
        sortDir: string = 'desc'
    ): Promise<AxiosResponse<any>> {
        return this.api.get(
            `/deal-service/api/deals/${dealPrivateId}/offers?page=${page}&size=${size}&sortField=${sortField}&sortDir=${sortDir}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * Get SMS template for pre-approval
     */
    public getPreApprovalSmsTemplate(token: string): Promise<AxiosResponse<any>> {
        return this.api.get(
            `/template-service/api/templates/PREAPPROVAL_SMS`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * Get Email template for pre-approval
     */
    public getPreApprovalEmailTemplate(token: string): Promise<AxiosResponse<any>> {
        return this.api.get(
            `/template-service/api/templates/_PREAPPROVAL_EMAIL_`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * Get Email template for application created notification
     */
    public getApplicationCreatedEmailTemplate(token: string): Promise<AxiosResponse<any>> {
        return this.api.get(
            `/template-service/api/templates/APPLICATION_CREATED_EMAIL`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * Get Email template for decline notification
     */
    public getDeclineEmailTemplate(token: string): Promise<AxiosResponse<any>> {
        return this.api.get(
            `/template-service/api/templates/DECLINE_EMAIL`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * Get SMS template for decline notification
     */
    public getDeclineSmsTemplate(token: string): Promise<AxiosResponse<any>> {
        return this.api.get(
            `/template-service/api/templates/DECLINE_SMS`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * Dispatch SMS communication
     */
    public dispatchCommunication(
        token: string,
        payload: {
            requestId: string;
            action: string;
            contacts: any[];
            templates: any[];
        }
    ): Promise<AxiosResponse<any>> {
        return this.api.post(
            `/communication-service/api/communications/dispatch`,
            payload,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            }
        );
    }

    /**
     * Generate link and email for deal offers
     */
    public generateDealLinkAndEmail(
        token: string,
        dealPrivateId: string,
        payload: {
            ttlHours: number;
            locale: string;
            reuseIfActive: boolean;
            recipient: {
                name: string;
                email: string;
            };
            frontDomain: string;
        }
    ): Promise<AxiosResponse<any>> {
        return this.api.post(
            `/deal-service/api/deals/${dealPrivateId}/link-and-email`,
            payload,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            }
        );
    }

    /**
     * Get deal selection status
     */
    public async getDealSelectionStatus(
        token: string,
        dealPrivateId: string
    ): Promise<AxiosResponse<any>> {
        try {
            return await this.api.get(
                `/deal-service/api/deals/${dealPrivateId}/selection-status`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
        } catch (error) {
            // If the endpoint doesn't exist yet (404) or there's an auth issue (401),
            // return a default response indicating no selection
            if (error.response?.status === 404 || error.response?.status === 401) {
                console.warn(`Selection status endpoint returned ${error.response.status}. Returning default no-selection response.`);
                return {
                    data: {
                        hasSelection: false,
                        selectedOfferId: null
                    },
                    status: 200,
                    statusText: 'OK (simulated)',
                    headers: {},
                    config: error.config
                } as AxiosResponse<any>;
            }
            // Re-throw other errors
            throw error;
        }
    }

    /**
     * Get specific deal offer by ID
     */
    public getDealOfferById(
        token: string,
        dealPrivateId: string,
        offerId: string
    ): Promise<AxiosResponse<any>> {
        return this.api.get(
            `/deal-service/api/deals/${dealPrivateId}/offers/${offerId}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    public updateDeal(
        token: string,
        dealPrivateId: string,
        dealData: any): Promise<AxiosResponse<any>> {
        return this.api.put(`/deal-service/api/deals/${dealPrivateId}`,
            dealData,{ headers: {  
                Authorization: `Bearer ${token}` 
            } });
        }

    /**
     * Save declined reason for a deal
     */
    public saveDeclinedReason(
        token: string,
        dealId: string,
        reason: string
    ): Promise<AxiosResponse<any>> {
        return this.api.post(
            `/deal-service/api/declined-reasons`,
            { dealId, reason },
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * Get declined reason for a deal
     */
    public getDeclinedReason(
        token: string,
        dealId: string
    ): Promise<AxiosResponse<any>> {
        return this.api.get(
            `/deal-service/api/declined-reasons/deal/${dealId}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * Get funding document template
     */
    public getFundingDocumentTemplate(token: string): Promise<AxiosResponse<any>> {
        return this.api.get(
            `/template-service/api/templates/FUNDING_DOCUMENT`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * Create prefunding configuration
     */
    public createPrefunding(
        token: string,
        dealPrivateId: string,
        prefundingData: {
            advanceAmount: number;
            grossAdvanceAmount: number;
            netPayback: number;
            factor: number;
            monthlyPayment: number;
            selectionDate: string;
            term: number;
            dayOfMonth: number;
            firstInstallmentDate: string;
        }
    ): Promise<AxiosResponse<any>> {
        // Generate idempotency key
        const idempotencyKey = `prefunding-${dealPrivateId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        
        return this.api.post(
            `/financial-service/api/financial/deals/${dealPrivateId}/prefunding`,
            prefundingData,
            { 
                headers: { 
                    Authorization: `Bearer ${token}`,
                    'idempotencykey': idempotencyKey
                } 
            }
        );
    }

    /**
     * Save payment plan
     */
    public savePaymentPlan(
        token: string,
        dealPrivateId: string,
        paymentPlanData: {
            installments: Array<{
                number: number;
                date: string;
                amount: number;
                balanceAfter: number;
                status: string;
            }>;
        }
    ): Promise<AxiosResponse<any>> {
        // Generate idempotency key
        const idempotencyKey = `payment-plan-${dealPrivateId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        
        return this.api.post(
            `/financial-service/api/financial/deals/${dealPrivateId}/payment-plan`,
            paymentPlanData,
            { 
                headers: { 
                    Authorization: `Bearer ${token}`,
                    'idempotencykey': idempotencyKey
                } 
            }
        );
    }

    /**
     * Update wire information
     */
    public updateWireInformation(
        token: string,
        dealPrivateId: string,
        dealPublicId: string,
        wireData: {
            status: string;
            comments?: string;
            referenceId?: string;
            wiredDate?: string;
            wiredBy?: string;
        }
    ): Promise<AxiosResponse<any>> {
        // Generate idempotency key
        const idempotencyKey = `wire-${dealPrivateId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        
        return this.api.put(
            `/financial-service/api/financial/deals/${dealPrivateId}/wire?dealPublicId=${encodeURIComponent(dealPublicId)}`,
            wireData,
            { 
                headers: { 
                    Authorization: `Bearer ${token}`,
                    'idempotencykey': idempotencyKey
                } 
            }
        );
    }

    /**
     * Attach wire voucher document
     */
    public attachWireVoucher(
        token: string,
        dealPrivateId: string,
        documentId: string
    ): Promise<AxiosResponse<any>> {
        // Generate idempotency key
        const idempotencyKey = `wire-voucher-${dealPrivateId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        
        return this.api.post(
            `/financial-service/api/financial/deals/${dealPrivateId}/wire/voucher`,
            { documentId },
            { 
                headers: { 
                    Authorization: `Bearer ${token}`,
                    'idempotencykey': idempotencyKey
                } 
            }
        );
    }

    /**
     * Get prefunding configuration
     */
    public getPrefundingConfiguration(
        token: string,
        dealPrivateId: string
    ): Promise<AxiosResponse<any>> {
        return this.api.get(
            `/financial-service/api/financial/deals/${dealPrivateId}/prefunding`,
            { 
                headers: { 
                    Authorization: `Bearer ${token}`
                } 
            }
        );
    }

    /**
     * Get payment plan
     */
    public getPaymentPlan(
        token: string,
        dealPrivateId: string
    ): Promise<AxiosResponse<any>> {
        return this.api.get(
            `/financial-service/api/financial/deals/${dealPrivateId}/payment-plan`,
            { 
                headers: { 
                    Authorization: `Bearer ${token}`
                } 
            }
        );
    }

    /**
     * Get wire information
     */
    public getWireInformation(
        token: string,
        dealPrivateId: string
    ): Promise<AxiosResponse<any>> {
        return this.api.get(
            `/financial-service/api/financial/deals/${dealPrivateId}/wire`,
            { 
                headers: { 
                    Authorization: `Bearer ${token}`
                } 
            }
        );
    }

    /**
     * Search deals by status and substatus
     */
    public searchBySubstatus(
        token: string,
        status: string,
        substatus: string,
        page: number = 0,
        size: number = 10
    ): Promise<AxiosResponse<any>> {
        return this.api.get(
            `/deal-service/api/deals/search-by-substatus?status=${status}&substatus=${substatus}&page=${page}&size=${size}`,
            { 
                headers: { 
                    Authorization: `Bearer ${token}`
                } 
            }
        );
    }

    /**
     * Get substatus counts by status
     */
    public getSubstatusCounts(
        token: string,
        status: string
    ): Promise<AxiosResponse<any>> {
        return this.api.get(
            `/deal-service/api/deals/counts/substatus-by-status?status=${status}`,
            { 
                headers: { 
                    Authorization: `Bearer ${token}`
                } 
            }
        );
    }

     /**
     * Get deals counts by type (status, substatus, etc.)
     */
     public getDealsCounts(
        token: string,
        type: string = 'status'
    ): Promise<AxiosResponse<any>> {
        return this.api.get(
            `/deal-service/api/deals/counts?type=${type}`,
            { 
                headers: { 
                    Authorization: `Bearer ${token}`
                } 
            }
        );
    }

    /**
     * Get deal addendum configuration by deal private ID
     */
    public getDealAddendumByDealId(
        token: string,
        dealPrivateId: string
    ): Promise<AxiosResponse<any>> {
        return this.api.get(
            `/deal-service/api/deal-addendums/by-deal/${dealPrivateId}`,
            { 
                headers: { 
                    Authorization: `Bearer ${token}`
                } 
            }
        );
    }

    /**
     * Count deals by status
     */
    public countDealsByStatus(
        token: string,
        status: string
    ): Promise<AxiosResponse<any>> {
        // Use the existing search endpoint with a large page size to get all results
        // and return the total count from the response
        return this.api.get(
            `/deal-service/api/deals?status=${status}&page=0&size=1000`,
            { 
                headers: { 
                    Authorization: `Bearer ${token}`
                } 
            }
        );
    }

    // ==================== COLLECTIONS API ====================

    /**
     * Get collections summary for a deal
     */
    public getCollectionsSummary(
        token: string,
        dealPrivateId: string
    ): Promise<AxiosResponse<any>> {
        return this.api.get(
            `/financial-service/api/financial/deals/${dealPrivateId}/collections/summary`,
            { 
                headers: { 
                    Authorization: `Bearer ${token}`
                } 
            }
        );
    }

    /**
     * Register a payment for a deal (with optional file attachment)
     */
    public registerPayment(
        token: string,
        dealPrivateId: string,
        paymentData: any,
        file?: File
    ): Promise<AxiosResponse<any>> {
        // Generate idempotency key
        const idempotencyKey = `${dealPrivateId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        // If there's a file, use FormData (legacy behavior)
        if (file) {
            const formData = new FormData();
            
            // Add payment data as JSON blob
            formData.append('payment', new Blob(
                [JSON.stringify(paymentData)],
                { type: 'application/json' }
            ));
            
            // Add file
            formData.append('attachment', file);

            return this.api.post(
                `/financial-service/api/financial/deals/${dealPrivateId}/collections`,
                formData,
                { 
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Idempotency-Key': idempotencyKey
                    } 
                }
            );
        } else {
            // If no file, send as JSON directly
            return this.api.post(
                `/financial-service/api/financial/deals/${dealPrivateId}/collections`,
                paymentData,
                { 
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                        'Idempotency-Key': idempotencyKey
                    } 
                }
            );
        }
    }

    /**
     * List payments for a deal with pagination
     */
    public listPayments(
        token: string,
        dealPrivateId: string,
        page: number = 0,
        size: number = 20,
        sort: string = 'paymentDate,desc'
    ): Promise<AxiosResponse<any>> {
        return this.api.get(
            `/financial-service/api/financial/deals/${dealPrivateId}/collections?page=${page}&size=${size}&sort=${sort}`,
            { 
                headers: { 
                    Authorization: `Bearer ${token}`
                } 
            }
        );
    }

    /**
     * Search/filter payments for a deal
     */
    public searchPayments(
        token: string,
        dealPrivateId: string,
        filters: {
            dateFrom?: string;
            dateTo?: string;
            reference?: string;
        }
    ): Promise<AxiosResponse<any>> {
        const params = new URLSearchParams();
        
        if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
        if (filters.dateTo) params.append('dateTo', filters.dateTo);
        if (filters.reference) params.append('reference', filters.reference);

        return this.api.get(
            `/financial-service/api/financial/deals/${dealPrivateId}/collections/search?${params.toString()}`,
            { 
                headers: { 
                    Authorization: `Bearer ${token}`
                } 
            }
        );
    }

    /**
     * Update remaining amount for a deal
     */
    public updateRemainingAmount(
        token: string,
        dealPrivateId: string,
        remainingAmount: number
    ): Promise<AxiosResponse<any>> {
        return this.api.patch(
            `/deal-service/api/deals/${dealPrivateId}/remaining-amount`,
            { remainingAmount },
            { 
                headers: { 
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                } 
            }
        );
    }

    /**
     * Get audit events (history) for a deal
     */
    public getDealAuditEvents(
        token: string,
        dealPrivateId: string
    ): Promise<AxiosResponse<any>> {
        return this.api.get(
            `/deal-service/api/v1/deals/${dealPrivateId}/audit-events`,
            { 
                headers: { 
                    Authorization: `Bearer ${token}`
                } 
            }
        );
    }

    /**
     * Update has-blank-check flag for a deal
     */
    public updateHasBlankCheck(
        token: string,
        dealPrivateId: string,
        hasBlankCheck: boolean
    ): Promise<AxiosResponse<any>> {
        return this.api.patch(
            `/deal-service/api/deals/${dealPrivateId}/has-blank-check`,
            { hasBlankCheck },
            { 
                headers: { 
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                } 
            }
        );
    }
}

export default new DealService();
